import {
  getDocument,
  GlobalWorkerOptions,
  TextLayer,
} from "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.6.82/+esm";

GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.6.82/pdf.worker.min.mjs";

class HcPdfViewer extends HTMLElement {
  constructor() {
    super();
    this.pages = [];
    this.src = this.getAttribute("src");
    if (this.hasAttribute("max-width")) {
      this.maxWidth = this.getAttribute("max-width");
    }
    window.addEventListener("resize", this.onResize.bind(this));
  }

  connectedCallback() {
    // render the viewer only if it has not been rendered yet
    if (this.loaded) return;
    this.render();
  }

  render() {
    this.loadPdf();
    this.loaded = true;
  }

  async loadPdf() {
    const pdf = await getDocument(this.src).promise;
    this.pdf = pdf;
    this.renderAllPages();
  }

  renderAllPages() {
    this.innerHTML = "";
    this.classList.add("loaded");
    const pageCount = this.pdf.numPages;
    this.dispatchEvent(
      new CustomEvent("pdf:loaded", {
        detail: { pdf: this.pdf },
        bubbles: true,
      }),
    );
    for (let i = 1; i <= pageCount; i++) {
      this.renderPage(i);
    }
  }
  loadPdfCanvas() {
    const canvas = document.createElement("canvas");
    const pageContainer = document.createElement("div");
    pageContainer.className = "pageContainer";
    pageContainer.appendChild(canvas);
    this.appendChild(pageContainer);
    return { canvas, pageContainer};
  }

  async renderPage(pageNumber) {
    const {canvas, pageContainer} = this.loadPdfCanvas();

    const page = await this.pdf.getPage(pageNumber);
    this.pages.push(page);

    // Support HiDPI-screens.
    var outputScale = window.devicePixelRatio || 1;
    let targetWidth = window.innerWidth;
    if (this.maxWidth) {
      targetWidth = Math.min(window.innerWidth, this.maxWidth);
    }
    targetWidth = targetWidth * outputScale;

    const scaleToWindow = targetWidth / page.getViewport({ scale: 1 }).width;

    const viewport = page.getViewport({ scale: scaleToWindow });

    const context = canvas.getContext("2d");

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    this.renderTextLayer(page, pageContainer, scaleToWindow / outputScale);
    page.custom = {
      pageContainer,
    }
  }

  async renderPageDetails(page, rawDims, scale) {
    this.renderTextLayer(page, rawDims, scale);
  }

  onResize() {
    if (!this.pages.length) return;

    for (const page of this.pages) {
      this.onResizePage(page);
    }
  }

  onResizePage(page) {
    const { pageContainer } = page.custom;
    let targetWidth = window.innerWidth;
    targetWidth = Math.min(window.innerWidth, this.maxWidth);
    // Support HiDPI-screens.
    var outputScale = window.devicePixelRatio || 1;
    targetWidth = targetWidth * outputScale;
    const scale = targetWidth / page.getViewport({ scale: 1 }).width;


    const viewport = page.getViewport({ scale: scale });
    const canvas = pageContainer.querySelector("canvas");
    const context = canvas.getContext("2d");

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    page.render(renderContext);

    this.renderTextLayer(page, pageContainer, scale / outputScale);
  }

  async renderTextLayer(page, pageContainer, scale) {
    const textLayerDiv = pageContainer.querySelector(".textLayer") || document.createElement("div");
    textLayerDiv.className = "textLayer";
    pageContainer.appendChild(textLayerDiv);

    const textContent = await page.getTextContent();
    const viewport = page.getViewport({
      scale: scale 
    });

    textLayerDiv.style.setProperty(
        "--scale-factor",
        scale 
      );

    if (page.custom.textLayer) {
      await page.custom.textLayer.update({
        viewport,
      })
      return;
    }
    const textLayer = new TextLayer({
      textContentSource: textContent,
      container: textLayerDiv,
      viewport: viewport,
    });

    await textLayer.render();
  }
}

customElements.define("pdf-viewer", HcPdfViewer);
